import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    borders,
    colors,
    fonts,
    scrollbars,
} from "../../components/common/mixins";
import styled from "styled-components";
import { ArrowDown, Search } from "../../components/icons";
import { CountryCodesOption } from "../../types/components";
import { useReservationResources } from "../../pageComponents/reservationPage/hooks/useReservationResources";

const flagSrc = (code: string): string =>
    `https://cdn.viva.gr/app/images/icons/flags/${code}.svg`;

interface CountryCodeDropdownProps {
    options: CountryCodesOption[];
    value?: CountryCodesOption | null;
    defaultValue?: string;
    onChange?: (option: CountryCodesOption) => void;
    disabled?: boolean;
}

const Wrapper = styled.div`
  position: relative;
  min-width: 84px;
`;

const Control = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  ${borders.formActiveBorder()};
  background: #fff;
  height: 48px;

  @media (max-width: 900px) {
    height: 40px;
  }
  cursor: pointer;
  box-sizing: border-box;
  text-align: left;
  color: ${colors.darkText};
  transition: border-color 0.2s ease;

  &:focus-visible {
    outline: none;
    border: 1px solid ${colors.primary};
    padding: 11px;
  }

  &:disabled {
    ${borders.lightGrayBorder()};
    cursor: not-allowed;
  }
`;

const RotatingArrow = styled.div<{ $open: boolean }>`
  display: flex;
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: #fff;
  ${borders.formActiveBorder()};
  border-radius: 8px;
  z-index: 100;
  overflow: hidden;
  padding: 12px;
  padding-top: 0;
  max-height: 296px;
  overflow-y: auto;
  box-shadow: 0px 4px 4px 0px #00000014;
  ${scrollbars.thin()}
`;

const SearchWrapper = styled.div`
  top: 0;
  background: #fff;
  z-index: 10;
  padding-top: 12px;
  padding-bottom: 8px;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  ${borders.formActiveBorder()};
  border-radius: 24px;
`;

const SearchInput = styled.input`
  && {
    flex: 1;
    border: none;
    outline: none;
    padding: 0;
    ${fonts.normalTextSmall()};
    font-size: 16px !important; /* prevents iOS auto-zoom on focus */

    background: transparent;
    &::placeholder {
      ${fonts.formPlaceholderSmall()};
    }
  }
`;

const OptionRow = styled.button`
  ${fonts.normalText()};
  width: 100%;
  text-align: left;
  border-radius: 4px;
  border: none;
  background: transparent;

  cursor: pointer;
  transition: background 0.15s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
`;

const FlagIcon = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const FlagPlaceholder = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #e8e8e8;
`;

function LazyFlag({ code }: { code: string }) {
    const ref = useRef<HTMLSpanElement>(null);
    const [src, setSrc] = useState("");

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const root = document.getElementById("country-code-pop-up");
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setSrc(flagSrc(code));
                    observer.disconnect();
                }
            },
            { root, threshold: 0 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [code]);

    if (!src) return <FlagPlaceholder ref={ref} />;
    return <FlagIcon src={src} alt={code} />;
}

const OptionMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;

  &:hover {
    background: ${colors.lightPrimary};
    border-radius: 4px;
  }

  &:focus {
    outline: none;
    background: ${colors.lightPrimary};
    border-radius: 4px;
  }
`;

const CountryCode = styled.span`
  white-space: nowrap;
  margin: 8px;
`;

const EmptyState = styled.div`
  padding: 12px 16px;
  ${fonts.formPlaceholderSmall()};
`;

export function CountryCodeDropdown({
    options = [],
    value = null,
    defaultValue,
    onChange,
    disabled = false,
}: CountryCodeDropdownProps) {
    const [open, setOpen] = useState(false);
    const [internalValue, setInternalValue] = useState<CountryCodesOption | null>(
        null,
    );
    const r = useReservationResources();
    const selected = value ?? internalValue;
    const [searchValue, setSearchValue] = useState("");
    const wrapperRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (value != null) return;
        if (!defaultValue) return;
        const found = options.find((o) => o.code === defaultValue) ?? options[0];
        if (found) setInternalValue(found);
    }, [defaultValue, options]);

    // close on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // focus search on open + clear search
    useEffect(() => {
        if (!open) return;
        setSearchValue("");
        requestAnimationFrame(() => searchRef.current?.focus?.());
    }, [open]);

    const filteredOptions = useMemo(() => {
        const searchedOption = searchValue.trim().toLowerCase();
        if (!searchedOption) return options;

        const normalizedSearch = searchedOption.startsWith("+")
            ? searchedOption.slice(1)
            : searchedOption;

        return options.filter((o) => {
            const hay = `${o.prefix} ${o.code}`.toLocaleLowerCase();
            return hay.includes(normalizedSearch);
        });
    }, [options, searchValue]);

    const handleSelect = (opt: CountryCodesOption) => {
        if (value == null) setInternalValue(opt);
        setOpen(false);
        onChange?.(opt);
    };

    return (
        <Wrapper id="country-code-wrapper" ref={wrapperRef}>
            <Control id="country-code-btn"
                type="button"
                onClick={() => !disabled && setOpen((o) => !o)}
                disabled={disabled}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {selected && (
                        <FlagIcon src={flagSrc(selected.code)} alt={selected.code} />
                    )}
                </div>

                <RotatingArrow $open={open}>
                    <ArrowDown />
                </RotatingArrow>
            </Control>

            {open && (
                <Menu id="country-code-pop-up">
                    <SearchWrapper>
                        <SearchInputWrapper>
                            <Search />
                            <SearchInput
                                id="country-code-input"
                                ref={searchRef}
                                type="text"
                                placeholder={r.Search}
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                        </SearchInputWrapper>
                    </SearchWrapper>

                    {filteredOptions.length === 0 ? (
                        <EmptyState>{r.NoData}</EmptyState>
                    ) : (
                        filteredOptions.map((opt) => (
                            <OptionRow id="country-option-btn" key={`${opt.id}`} onClick={() => handleSelect(opt)}>
                                <LazyFlag code={opt.code} />
                                <OptionMain>
                                    <CountryCode>+{opt.prefix}</CountryCode>
                                </OptionMain>
                            </OptionRow>
                        ))
                    )}
                </Menu>
            )}
        </Wrapper>
    );
}